// StepModules — Phase 5 step 5 (V1 scaffold).
// Lets the operator toggle which modules the new tenant boots with.
// Auto-activation rules (e.g. VAT=true → ZATCA on) land in Option 🅲.
import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Sparkles, ChevronRight } from 'lucide-react';

const MODULES = [
  { id: 'invoicing',      label: 'Invoicing & AR',          desc: 'Invoices · payments · credit notes',          recommended: true },
  { id: 'procurement',    label: 'Procurement & AP',        desc: 'Purchase orders · supplier bills',            recommended: true },
  { id: 'projects',       label: 'Projects & contracts',    desc: 'Contracts · progress claims · cost centres',  recommended: true },
  { id: 'payroll',        label: 'Payroll',                  desc: 'Employees · monthly runs · GOSI',             recommended: false },
  { id: 'hr',             label: 'HR',                      desc: 'Employees · leaves · documents',               recommended: false },
  { id: 'inventory',      label: 'Inventory & assets',      desc: 'Stock · MAR · MIR · depreciation',            recommended: false },
  { id: 'vat_zatca',      label: 'VAT & ZATCA e-invoicing', desc: 'Standard rate 15% · phase 2 submission',      recommended: true },
  { id: 'cfo_intel',      label: 'CFO Intelligence',        desc: 'Anomaly · risk · recommendations · decisions', recommended: true },
];

// Mirror of MODULE_ACTIVATION_RULES in services/company_onboarding_service.py
// Enabling key → force-enable each dep so the UI matches the server.
const DEPENDS_ON = {
  vat_zatca:   ['invoicing'],
  projects:    ['invoicing'],
  procurement: ['invoicing'],
  payroll:     ['hr'],
  inventory:   ['procurement'],
};

function resolveModules(enabled) {
  const out = { ...enabled };
  let changed = true;
  while (changed) {
    changed = false;
    for (const [trigger, deps] of Object.entries(DEPENDS_ON)) {
      if (out[trigger]) {
        for (const dep of deps) {
          if (!out[dep]) { out[dep] = true; changed = true; }
        }
      }
    }
  }
  return out;
}

export default function StepModules({ initialData, onSubmit, busy }) {
  const [enabled, setEnabled] = useState(() => {
    const init = {};
    MODULES.forEach((m) => { init[m.id] = m.recommended; });
    return init;
  });

  useEffect(() => {
    if (initialData?.enabled) {
      const h = setTimeout(() => setEnabled((cur) => ({ ...cur, ...initialData.enabled })), 0);
      return () => clearTimeout(h);
    }
  }, [initialData]);

  const toggle = (id) => setEnabled((e) => {
    const next = { ...e, [id]: !e[id] };
    // If user is ENABLING a module → cascade dependencies on.
    // If DISABLING → just leave deps as they are (user may have other reasons).
    return next[id] ? resolveModules(next) : next;
  });

  return (
    <div data-testid="step-modules" className="space-y-5">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-indigo-50 text-indigo-700"><Sparkles size={18} /></div>
        <div>
          <h2 className="text-lg font-bold text-slate-900">Pick the modules to boot with</h2>
          <p className="text-sm text-slate-500 mt-0.5">You can toggle these later in Settings → Modules.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {MODULES.map((m) => {
          const on = !!enabled[m.id];
          // Auto-on indicator: this module is on BECAUSE another module
          // requires it. Computed by looking at DEPENDS_ON entries.
          const requiredBy = Object.entries(DEPENDS_ON)
            .filter(([trig, deps]) => deps.includes(m.id) && enabled[trig])
            .map(([trig]) => trig);
          const forced = on && requiredBy.length > 0;
          return (
            <button
              type="button"
              key={m.id}
              data-testid={`module-${m.id}`}
              onClick={() => toggle(m.id)}
              className={`text-left px-4 py-3 rounded-lg border transition ${
                on ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-slate-900">{m.label}</span>
                <span className={`text-[10px] uppercase tracking-wider font-bold ${
                  on ? 'text-emerald-700' : 'text-slate-400'
                }`}>{on ? 'ON' : 'OFF'}</span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{m.desc}</p>
              {forced && (
                <p className="text-[10px] text-indigo-700 mt-1.5 font-medium" data-testid={`module-forced-${m.id}`}>
                  Auto-enabled · required by {requiredBy.join(', ')}
                </p>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex justify-end">
        <Button
          onClick={() => onSubmit({ enabled })}
          disabled={busy}
          data-testid="modules-save-btn"
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          Save & continue <ChevronRight size={14} className="ml-1.5" />
        </Button>
      </div>
    </div>
  );
}
